import {useEffect, useRef} from 'react';
import type {User} from '@common-web/auth';
import {useAuthStore} from '@common-web/stores/authStore';
import {identifyUser, registerSuperProperties, resetAnalytics} from '@common-web/lib/analytics';

export type AnalyticsUserType = 'internal' | 'client' | 'group' | 'broker' | 'tpa';

function analyticsUserType(user: User): AnalyticsUserType {
  if (user.cbsInternalProfile) return 'internal';
  if (user.clientProfile) return 'client';
  if (user.brokerProfile) return 'broker';
  if (user.tpaProfile) return 'tpa';
  return 'group';
}

/**
 * Subscribes to auth and identifies the person in PostHog. Mount under
 * AnalyticsProvider on authenticated apps. Do not mount on public enrollment.
 *
 * Distinct id is `user.id`. Email and name are person traits so replay is
 * filterable for support. `reset()` only runs after a session was identified,
 * so the initial anonymous login pageview is not split.
 */
export function AnalyticsIdentify() {
  const user = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);
  const identifiedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    if (user) {
      const userType = analyticsUserType(user);
      identifyUser(user.id, {
        email: user.email,
        name: user.name,
        user_type: userType,
      });
      registerSuperProperties({user_type: userType});
      identifiedIdRef.current = user.id;
      return;
    }

    if (identifiedIdRef.current) {
      resetAnalytics();
      identifiedIdRef.current = null;
    }
  }, [user, isLoading]);

  return null;
}
